import * as E from 'fp-ts/lib/Either';
import {DBSchema, deleteDB, IDBPDatabase, IDBPTransaction, openDB, StoreNames} from 'idb';
import * as t from 'io-ts';
import memoize from 'lru-memoize';
import {Subject} from 'rxjs';

import {
  globalDocument,
  globalWindow,
  reloadBrowser,
} from '../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {
  clearOrCreateStore,
  isDatabaseClosingError,
  isQuotaExceededError,
  migrateStoreData,
  safeDeleteStore,
} from '../../../libs/shared/core/src/helpers/idb/idbHelpers';
import {
  annotateTaskType,
  TaskTypesEnum,
} from '../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {ExtendedError} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {ApiType} from '../../../libs/shared/core/src/models/api/commonApiTypes';
import {
  DefaultValueFallbackModesEnum,
  makeDefaultValueFallbackModeType,
} from '../../../libs/shared/core/src/models/api/types/defaultValueType';
import {makeStrictModeType} from '../../../libs/shared/core/src/models/api/types/safeTypesHelpers';
import {leaveBreadcrumb, reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {debug} from '../../../libs/shared/core/src/services/debug/debug';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {withTimeoutAsync} from '../../../src/helpers/asyncHelpers';
import {RDataGroup} from '../../../src/models/api/dataGroupModel';
import {getIdentity, setIdentity} from '../storage/identity';
import {OfflineResourceCache, OfflineResourceCacheStats, ResourceRecord} from './offlineResourcesCommon';

/*
 * Constants.
 */

const dbTimeout = 5 * 1000;

type ID = string | number;

interface ResourceSchema<KEY> {
  key: KEY;
  value: ResourceRecord;
  indexes: {
    timestamp: number;
  };
}

interface Schema extends DBSchema {
  conversations: never; // This store was used in the past then was decommissioned but should make a come back in the future (⚠️ do not forget to clear it before reusing it)
  conversationContents: ResourceSchema<number>;
  draftMessages: ResourceSchema<string>;
  draftComments: ResourceSchema<string>;
  lruTable: never;
}

// The list of all the indexeddb stores, historical or not.
const storeNames = [
  'lruTable',
  'conversations',
  'conversationContents',
  'draftMessages',
  'draftComments',
] as const;

type StoreName = (typeof storeNames)[number];

enum DatabaseCloseReasonsEnum {
  UPGRADE = 'upgrade',
  DELETE = 'delete',
  UNKNOWN = 'unknown',
}

let currentDB: IDBPDatabase<Schema> | undefined;
let dbCloseReason: DatabaseCloseReasonsEnum | undefined;

async function openDatabaseWithTimeout(options = {maybeRetry: true}) {
  const timeBefore = performance.now();
  let didTimeout = false;

  const result = await withTimeoutAsync(
    () => openDatabase(options),
    dbTimeout,
    () => {
      didTimeout = true;
      return false;
    },
  );

  const timeElapsed = performance.now() - timeBefore;
  logger.info('Offline resource database opened', {
    duration: timeElapsed,
    did_succeed: result,
    did_timeout: didTimeout,
  });
  return result;
}

async function openDatabase(options = {maybeRetry: true}) {
  try {
    currentDB = await openDB('offlineResources', 4, {
      upgrade: upgradeDB,

      blocking() {
        // Should never happen.
        if (!currentDB) {
          return;
        }

        // If this DB is preventing a newer DB in another tab from opening, give the other one the priority and consider this cache dead.
        // Closing this instance lets the other tab open its newer DB.
        closeDatabase(DatabaseCloseReasonsEnum.UPGRADE);

        // Another tab has a more recent DB version. This means this outdated DB can no longer be used for offline features
        // such as draft comment/message persisting. Since some of these features are critical and the user is likely looking at the other tab,
        // reload to get the latest app version, and these features working again.
        // Only start the reload process after bugsnag successfuly sent this report, as we don't want the http request to be cancelled.
        reportToBugsnag(
          new Error('An older app version prevents an upgrade of the indexedDB version, reloading.'),
          {metadata: {document_hidden: globalDocument.hidden}},
          () => reloadBrowser(),
        );
      },
    });
  } catch (err) {
    // The currently loaded code uses a version smaller than the local database's (most likely due to a rollback),
    // recreate the DB at the smaller version, from scratch.
    if (err instanceof DOMException && err.name === 'VersionError' && options.maybeRetry) {
      reportToBugsnag(new Error(`Reset offline resources: ${err.message}`));
      return resetOfflineResources({shouldSetup: true});
    }

    leaveBreadcrumb(`[Offline Resources] Unable to open database`, {
      error: err,
    });

    return false;
  }

  return true;
}

function upgradeDB(
  db: IDBPDatabase<Schema>,
  oldVersion: number,
  _newVersion: number,
  tx: IDBPTransaction<Schema, Array<StoreNames<Schema>>, 'versionchange'>,
) {
  /* eslint-disable no-fallthrough */
  // The fallthrough behavior is useful here, to apply needed evolutions sequentially if the DB is not the second to last version.
  switch (oldVersion) {
    case 0: {
      const lruTable = db.createObjectStore('lruTable');
      lruTable.createIndex('lastUsedAt', 'lastUsedAt', {unique: false});
      lruTable.createIndex('storeName', 'storeName', {unique: false});
    }

    case 1:
      clearOrCreateStore('conversationContents', db, tx);

    case 2:
      clearOrCreateStore('draftMessages', db, tx);
      clearOrCreateStore('draftComments', db, tx);

    case 3:
      lrwMigration(db, tx);

    default:
      break;
  }
  /* eslint-enable no-fallthrough */
}

function lrwMigration(
  db: IDBPDatabase<Schema>,
  tx: IDBPTransaction<Schema, Array<StoreNames<Schema>>, 'versionchange'>,
) {
  // Hard delete the lru table: we no longer use it as we're switching from LRU to LRW.
  safeDeleteStore('lruTable', db);

  (['conversationContents', 'draftMessages', 'draftComments'] as const).forEach((name) => {
    // The conversationContents store usually holds hundreds of objects and is not strategic: just clear it.
    if (name === 'conversationContents') {
      tx.objectStore(name).clear();
    } else {
      migrateStoreData(name, tx, (data) => {
        // At that time, the datagroup resource was directly stored as the value.
        if (RDataGroup.is(data)) {
          return {resource: data, timestamp: Date.now()};
        }

        const record = data as ResourceRecord | undefined;
        // Noop: Possibly the browser was restarted mid-migration previously.
        if (record && record.resource && RDataGroup.is(record.resource)) {
          return data;
        }

        return undefined;
      });
    }

    const store = tx.objectStore(name);

    if (!Array.from(store.indexNames).includes('timestamp')) {
      store.createIndex('timestamp', 'timestamp', {unique: false});
    }
  });
}

export async function deleteOfflineResourcesAsync() {
  closeDatabase(DatabaseCloseReasonsEnum.DELETE);

  await deleteDB('offlineResources');
}

interface ResetOfflineResourcesOptions {
  /** Whether the offline caching should be re-setup as part of the reset. */
  shouldSetup: boolean;
}

export async function resetOfflineResources(options: ResetOfflineResourcesOptions): Promise<boolean> {
  try {
    await deleteOfflineResourcesAsync();
  } catch (error) {
    // Do nothing.
  }

  if (options.shouldSetup) {
    return openDatabaseWithTimeout({maybeRetry: false});
  }

  return true;
}

export const canSupportOfflineResources = memoize(1)(async () => {
  if (!globalWindow.indexedDB) {
    return false;
  }

  const timeBefore = performance.now();
  let didTimeout = false;

  // indexedDB is present, but can we actually use it? i.e some browsers sneakily disable (e.g, they throw errors) mutations in incognito mode.
  const result = await withTimeoutAsync(
    async () => {
      try {
        await deleteDB('temp');
        // If we can perform an operation using indexeddb, it's working.
        return true;
      } catch (err) {
        // If we get an error, indexeddb is not working.
        return false;
      }
    },
    // Let's allow 5 seconds to check if indexeddb is working.
    dbTimeout,
    // If we timeout, indexeddb is not working.
    () => {
      didTimeout = true;
      return false;
    },
  );

  const timeElapsed = performance.now() - timeBefore;
  debug(
    'offline',
    `Determined whether offline resources can be used in ${Math.round(timeElapsed)}ms: ${result}`,
  );
  logger.info('Offline resource check completed', {
    duration: timeElapsed,
    can_support_offline_resources: result,
    did_timeout: didTimeout,
  });

  return result;
});

export const setupOfflineResources = memoize(1)(async (userId: number, isUserVisitor: boolean) => {
  // TODO: Allow offline resources to work with multiple users logged in at the same time on the same domain, otherwise
  // different app instances will race to grab the database.
  if (isUserVisitor) {
    return false;
  }

  const isSupported = await canSupportOfflineResources();
  if (!isSupported) {
    return false;
  }

  const lastKnownUserId = getIdentity();

  if (lastKnownUserId && lastKnownUserId !== userId) {
    await deleteOfflineResourcesAsync();
  }

  try {
    setIdentity(userId);
  } catch (err) {
    // Ignore.
  }

  return await openDatabaseWithTimeout();
});

export function createOfflineCache<STORENAME extends StoreName, RESOURCE extends object>(
  storeName: STORENAME,
  apiType: ApiType<t.Type<RESOURCE, unknown>>,
  getUpdateTimestamp: (resource: RESOURCE) => number,
  getMaxEntryCountAsync?: () => Promise<number>,
): OfflineResourceCache<Schema[STORENAME]['key'], RESOURCE> {
  // Record when resource from the current session enter the cache to troubleshoot cache issues.
  const mapLastFetchSize = 1000;
  const lastFetchMap = new Map<ID, number>();
  const addedIds$ = new Subject<ID>();

  async function readAsync(id: ID, shouldLog = true): Promise<RESOURCE | undefined> {
    return handleDatabaseErrors(async () => {
      if (!currentDB) {
        return undefined;
      }

      const record = await currentDB.get(storeName, id);

      if (!record) {
        return undefined;
      }

      if (shouldLog) {
        debug('offline', `readAsync ${storeName}`, id, record.resource);
      }

      return validateResource(record.resource);
    });
  }

  // Note that Edge doesn't support getAll().
  async function readAllAsync(): Promise<ReadonlyArray<RESOURCE>> {
    const entries = await handleDatabaseErrors(async () => {
      if (!currentDB) {
        return undefined;
      }

      const result = [];
      const decodedResult = [];
      let cursor = await currentDB.transaction(storeName).store.openCursor();

      while (cursor) {
        const nextCursor = cursor.continue();
        const rawResource = cursor.value.resource;
        const decodedResource = validateResource(rawResource);

        result.push(rawResource);
        if (decodedResource) {
          decodedResult.push(decodedResource);
        }

        cursor = await nextCursor;
      }

      debug('offline', `readAllAsync ${storeName}`, {decodedResult, result});

      return decodedResult;
    });
    return entries || [];
  }

  async function readAllIdsAsync(): Promise<ReadonlyArray<ID>> {
    const ids = await handleDatabaseErrors(async () => {
      if (!currentDB) {
        return undefined;
      }

      return currentDB.getAllKeys(storeName);
    });
    return ids || [];
  }

  async function readStatsAsync(): Promise<OfflineResourceCacheStats> {
    const stats = await handleDatabaseErrors(async () => {
      if (!currentDB) {
        return undefined;
      }

      const {store} = currentDB.transaction(storeName, 'readonly');
      const [minTimestamp, maxTimestamp, count] = await Promise.all([
        store
          .index('timestamp')
          .openCursor()
          .then((cursor) => (cursor ? cursor.value.timestamp : undefined)),
        store
          .index('timestamp')
          .openCursor(undefined, 'prev')
          .then((cursor) => (cursor ? cursor.value.timestamp : undefined)),
        store.count(),
      ] as const);

      return {
        minTimestamp,
        maxTimestamp,
        count,
      };
    });
    return (
      stats || {
        minTimestamp: undefined,
        maxTimestamp: undefined,
        count: 0,
      }
    );
  }

  function getCacheResponseInfo(id: ID, resource: RESOURCE) {
    // If resource was cached before the session started, just return 0.
    const fetchedAt = lastFetchMap.get(id) || 0;
    const contentUpdatedAt = getUpdateTimestamp(resource);

    return {fetchedAt, contentUpdatedAt};
  }

  async function readIfLatestAsync(id: ID, getLatestTimestamp: () => number | undefined) {
    const resource = await readAsync(id, false);
    const resourceTimestamp = resource && getUpdateTimestamp(resource);

    if (!resourceTimestamp) {
      return undefined;
    }

    const latestTimestamp = getLatestTimestamp() || Infinity;
    const isLatest = resourceTimestamp >= latestTimestamp;
    const result = isLatest ? resource : undefined;

    debug('offline', 'readIfLatestAsync', storeName, id, {
      isLatest,
      resource,
      resourceTimestamp,
      latestTimestamp,
    });

    return result;
  }

  function addAsync(id: ID, resource: RESOURCE, timestamp = Date.now()) {
    return handleWriteErrors(async () => {
      if (!currentDB) {
        return;
      }

      debug('offline', `addAsync ${storeName}`, id, resource);

      // TODO: Use apiType.runtimeType.encode to serialize the resource.
      const record = {resource, timestamp};

      await currentDB.put(storeName, record, id);

      saveFetchDate(id);
      addedIds$.next(id);

      maybeTryEvictLeastRecentRecords();
    });
  }

  async function canAddTimestampAsync(timestamp: number) {
    const canAddTimestamp = await handleDatabaseErrors(async () => {
      // If we don't have a database, we can't cache anything.
      if (!currentDB) {
        return false;
      }

      // If we don't have a maximum, we can cache everything.
      if (!getMaxEntryCountAsync) {
        return true;
      }

      // Count the number of entries.
      const [maxEntryCount, entryCount] = await Promise.all([
        getMaxEntryCountAsync(),
        currentDB.transaction(storeName, 'readonly').store.count(),
      ]);

      // If we haven't reached our limit, we can cache everything.
      if (entryCount < maxEntryCount) {
        return true;
      }

      // Find the least recent timestamp.
      const cursor = await currentDB.transaction(storeName, 'readonly').store.index('timestamp').openCursor();

      // This shouldn't happen.
      if (!cursor) {
        return true;
      }

      const leastRecentTimestamp = cursor.value.timestamp;
      return timestamp > leastRecentTimestamp;
    });
    return Boolean(canAddTimestamp);
  }

  /*
   * If we display a stale resource that we think is fresh, it is useful to know when
   * the resource was last fetched, so we save it to a local map.
   * If the resource was fetched in a previous session, we won't know when but it's OK for now.
   */
  function saveFetchDate(id: ID) {
    lastFetchMap.set(id, Date.now());

    // Prevent the map from becoming too big. This takes about 15ms.
    if (lastFetchMap.size < mapLastFetchSize) {
      return;
    }

    const sortedlastFetchAts = [...lastFetchMap.values()].sort((a, b) => b - a);
    const threshold = sortedlastFetchAts[mapLastFetchSize / 2];

    lastFetchMap.forEach((lastFetchAt, key) => {
      if (lastFetchAt <= threshold) {
        lastFetchMap.delete(key);
      }
    });
  }

  function removeAsync(id: ID) {
    return handleWriteErrors(async () => {
      if (!currentDB) {
        return;
      }

      debug('offline', `removeAsync ${storeName}`, id);

      await currentDB.delete(storeName, id);
    });
  }

  const runtimeTypeWithDefaultValues = makeStrictModeType(
    makeDefaultValueFallbackModeType(apiType.runtimeType, DefaultValueFallbackModesEnum.SILENT_FALLBACK),
  );

  function validateResource(input: unknown) {
    // Only decode: the resource was already parsed when it was added to the cache.
    const validatedResource = runtimeTypeWithDefaultValues.decode(input);
    return E.getOrElse<unknown, RESOURCE | undefined>(() => undefined)(validatedResource);
  }

  // To make sure we don't have multiple cache eviction transactions waiting for each other.
  let isEvicting = false;

  function maybeTryEvictLeastRecentRecords() {
    // If there already is a cache eviction in flight, move on.
    if (isEvicting) {
      return;
    }

    isEvicting = true;

    evictLeastRecentRecords()
      .catch((error) => {
        reportToBugsnag(new ExtendedError(`[offline] Error while evicting cache ${storeName}`, error));
      })
      .then(() => {
        isEvicting = false;
      });
  }

  async function evictLeastRecentRecords() {
    return handleWriteErrors(async () => {
      if (!currentDB || !getMaxEntryCountAsync) {
        return;
      }

      const maxEntryCount = await getMaxEntryCountAsync();
      const startTime = performance.now();

      // Use a readonly transaction to count the number of records, so we can get out of here quickly.
      const readTx = currentDB.transaction(storeName);
      const readCount = await readTx.store.count();

      if (readCount <= maxEntryCount) {
        const timeElapsed = `${Math.round(performance.now() - startTime)}ms`;
        debug('offline', `No need to evict ${storeName} (${timeElapsed})`);
        return;
      }

      // Use a readwrite transaction to evict records.
      const writeTx = currentDB.transaction(storeName, 'readwrite');
      // Usually we expect to evict one record, but if it's more than one do a re-count in this transaction to be safe.
      const writeCount = readCount - maxEntryCount === 1 ? readCount : await writeTx.store.count();

      const evictCount = writeCount - maxEntryCount;
      let cursor = null;
      const evictedIds = [];

      // Don't open/continue a cursor unless there is more to evict.
      while (evictedIds.length < evictCount) {
        // Use a key cursor to avoid fetching entire records/resources.
        cursor = await (cursor ? cursor.continue() : writeTx.store.index('timestamp').openKeyCursor());
        if (!cursor) {
          break;
        }

        // Evict the next oldest record.
        const id = cursor.primaryKey;
        evictedIds.push(id);
        writeTx.store.delete(id);
      }

      await writeTx.done;

      const timeElapsed = `${Math.round(performance.now() - startTime)}ms`;
      debug('offline', `Evicted ${storeName} (${timeElapsed})`, evictedIds);
    });
  }

  return {
    apiType,
    readAsync,
    readAllAsync,
    readAllIdsAsync,
    readStatsAsync,
    readIfLatestAsync,
    addAsync,
    canAddTimestampAsync,
    addedIds$,
    removeAsync,
    getCacheResponseInfo,
  };
}

export function areOfflineResourcesAvailable() {
  return currentDB !== undefined;
}

/*
 * Helpers.
 */

function closeDatabase(reason: DatabaseCloseReasonsEnum) {
  if (!currentDB) {
    return;
  }

  currentDB.close();
  dbCloseReason = reason;
  currentDB = undefined;
}

async function handleDatabaseErrors<T>(dbEffect: () => Promise<T>): Promise<T | undefined> {
  try {
    return await dbEffect();
  } catch (error) {
    if (!isDatabaseClosingError(error)) {
      throw error;
    }

    logger.error('Database access failed because it is closing', {
      closing_reason: dbCloseReason || DatabaseCloseReasonsEnum.UNKNOWN,
    });

    // Mark the DB as no longer usable.
    currentDB = undefined;

    return undefined;
  } finally {
    annotateTaskType(TaskTypesEnum.DATABASE_RESPONSE);
  }
}

async function handleWriteErrors(dbEffect: () => Promise<void>): Promise<void> {
  try {
    await handleDatabaseErrors(dbEffect);
  } catch (error) {
    if (!isQuotaExceededError(error)) {
      throw error;
    }

    // Mark the DB as no longer usable.
    // There's no way to recover from this error, at least with Chrome 75
    // as even trying to clear all the object stores or delete cursors one by one will throw another Quota Exceeded error.
    currentDB = undefined;
  }
}
